import DOMPurify from 'dompurify';
import { Fragment, useState, type CSSProperties, type ReactNode } from 'react';
import type {
  EstimateDocBlock,
  EstimateDocContentBlock,
  EstimateDocLayout,
  EstimateDocSlotBlock,
} from '@notifi/shared-types/estimate-doc-template';

import type { Estimate } from '@/types/estimate';
import { formatPhoneDisplay } from '@/lib/contacts/phone';
import { formatDateTimeInTimeZone, formatTimeZoneLabel } from '@/lib/date-values';
import { formatDateOnly } from '@/lib/dates/date-only';
import { formatQuantityWithUnit } from '@/lib/sales/quantity-display';
import {
  estimateAddressLines,
  estimateBillToParty,
  estimateCustomerVisibleTitle,
} from '@/lib/sales/estimate-document-address';
import { INCLUDED_PRICE_DISPLAY } from '@/lib/sales/line-economics';
import { getEstimateCustomerNotes } from '@/lib/sales/estimate-rendering';
import { lineDetailVisibility } from '@/lib/sales/line-detail-visibility';
import { hasReadableContent, resolveCustomerTerms } from '@/lib/terms/clientTerms';
import { CustomerTermsBody } from '@/components/public/CustomerTermsBody';
import { RICH_TEXT_CLASS } from '@/lib/rich-text';
import { isHtmlContent } from '@/lib/utils';
import { RichTextContent } from '@/components/molecules/rich-text-content';
import { taxBreakdownDisplayLabel } from '@/lib/tax/tax-utils';

import {
  brandingToCssVars,
  fieldLabel,
  isFieldVisible,
  labelStyle,
  valueStyle,
} from './blockStyle';
import {
  BlockSectionHeading,
  BlockShell,
  DetailRow,
  docLabelStyle,
  HAIRLINE,
  INK,
  INK_SOFT,
  LINE,
  MEMBER_SAVE,
  MONO,
  MUTED,
  SectionLabel,
  STRIKE,
} from './docChrome';
import { DocRichText } from './DocRichText';
import {
  computeEstimateDocMoney,
  fmtMoney,
  resolveEstimateDocRowAmounts,
  type EstimateDocMoney,
} from './estimateDocMoney';
import type { EstimateMembershipOffer } from '@/types/estimate';
import {
  estimateWithAcceptedMembershipOffer,
  membershipOfferAdjustmentForLines,
} from '@/lib/sales/membership-offer';

/**
 * The company identity a document renders with. Self-contained so this renderer
 * carries no dependency on the public quote page — the caller supplies the same
 * shape the page's `CompanyInfo` already has, or `null`.
 */
export interface CompanyInfo {
  name: string;
  logoUrl?: string | null;
  logoDataUri?: string | null;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  } | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  taxRegistrationNumber?: string | null;
  tagline?: string | null;
  contractorLicense?: string | null;
  regulatoryNotice?: string | null;
}

/**
 * The company identity the document actually prints: the design's own branding
 * wins over the tenant company, falling back to it when a field is blank. This
 * is what lets a design carry a Business Unit's name/contact/tax without a
 * separate per-BU branding screen. (Address is handled separately — the design
 * stores it as one free-text block; see `brandedAddressLines`.)
 */
function brandedCompany(
  company: CompanyInfo | null,
  branding?: EstimateDocLayout['branding'],
): CompanyInfo | null {
  if (!branding) return company;
  const pick = (o: string | undefined, c: string | null | undefined) =>
    o && o.trim() ? o.trim() : (c ?? null);
  return {
    ...(company ?? { name: 'Your Company' }),
    name: pick(branding.displayName, company?.name) ?? 'Your Company',
    logoUrl: company?.logoUrl ?? null,
    logoDataUri: company?.logoDataUri ?? null,
    email: pick(branding.email, company?.email),
    phone: pick(branding.phone, company?.phone),
    taxRegistrationNumber: pick(branding.taxRegistrationNumber, company?.taxRegistrationNumber),
    tagline: pick(branding.tagline, company?.tagline),
    contractorLicense: pick(branding.licenseNumber, company?.contractorLicense),
    regulatoryNotice: branding.regulatoryNotice ?? company?.regulatoryNotice,
  };
}

/** Address lines for the header/footer: the design's own address wins. */
function brandedAddressLines(
  company: CompanyInfo | null,
  branding?: EstimateDocLayout['branding'],
): string[] {
  const brand = branding?.address;
  const addr =
    brand && (brand.street || brand.city || brand.state || brand.zip || brand.country)
      ? brand
      : company?.address;
  return addr
    ? [addr.street, [addr.city, addr.state, addr.zip].filter(Boolean).join(', '), addr.country]
        .map((l) => l?.trim())
        .filter((l): l is string => Boolean(l))
    : [];
}

/** A photo/file attached to the estimate, resolved to a displayable URL. */
export interface EstimateDocAttachment {
  id: string;
  url: string;
  fileName?: string;
}

/**
 * Authoritative totals the customer page has already derived (subtotal,
 * membership savings + plan price, discount, member tax, grand total, deposit).
 * When supplied, `TotalsBlock` renders these EXACTLY instead of re-deriving from
 * the estimate — so the design document's totals are byte-identical to the
 * action sidebar and the classic document (the gold standard), including the
 * membership-accepted breakdown that a single money computation can't reproduce
 * without double-counting the savings. Omitted in the builder preview, which has
 * no membership acceptance and computes from the sample estimate.
 */
export interface EstimateDocTotalsOverride {
  subtotal: number;
  membershipSavings: number;
  membershipPlanPrice: number;
  membershipSetupFee?: number;
  /** Names the plan-price line (the plan being bought). */
  membershipPlanLabel: string;
  /**
   * Names the plans the savings come from, when that differs from the plan
   * being bought — an existing member accepting another plan saves through
   * both ("Gold + Platinum"). Absent → the plan-price label.
   */
  membershipSavingsLabel?: string;
  discountTotal: number;
  discountLabel: string;
  taxBreakdown: EstimateDocMoney['taxBreakdown'];
  taxTotal: number;
  /** Absent on an override that does not compute it; the row is then omitted. */
  taxableSubtotal?: number;
  grandTotal: number;
  depositAmount: number;
}

export interface EstimateDocumentRendererProps {
  layout: EstimateDocLayout;
  estimate: Estimate;
  company: CompanyInfo | null;
  /**
   * Photos/files attached to the estimate (fetched separately from the estimate
   * body). The `attachments` block renders these; omitted → the block is empty.
   */
  attachmentImages?: EstimateDocAttachment[];
  /**
   * Whether positioned interaction slots render their labelled placeholder.
   * Defaults to `true`, so the standalone document (preview / tests) keeps
   * showing the dashed slot markers. The public estimate page passes `false`:
   * there the real interactive slots (approve / pay-deposit / signature) come
   * from the page's action rail, so the document column must render nothing in
   * their place rather than a placeholder.
   */
  renderSlots?: boolean;
  /**
   * App-owned content that belongs ON the document sheet rather than after it.
   *
   * The renderer draws the sheet, so anything rendered as a sibling lands
   * BELOW it as a separate card — which is how the customer-facing payment plan
   * ended up looking detached from the estimate it belongs to. Blocks the design
   * engine owns cannot cover it: the plan is app state, not an authored block.
   */
  documentTrailing?: ReactNode;
  /**
   * The estimate's join-and-save membership offer, when one exists. Threaded so
   * that — exactly like the classic itemized document — the line-item table can
   * strike through each affected line's list price and print the member price +
   * "Member price · save $X" once the customer accepts the offer. The offer is
   * app-owned (accept toggle + billing selector live in the page's sidebar);
   * this renderer only reads it to price the lines.
   */
  membershipOffer?: EstimateMembershipOffer | null;
  /**
   * Whether the customer has accepted the membership offer. When true (and an
   * offer with a plan price is present), affected lines render member pricing
   * and the totals reflect the member-adjusted estimate.
   */
  showMemberPrice?: boolean;
  /**
   * Authoritative totals from the customer page; see `EstimateDocTotalsOverride`.
   * When present, the totals block renders these instead of the money it derives.
   */
  totalsOverride?: EstimateDocTotalsOverride;
  /** Proposals honor each quoted line's hidePrice flag. Opt-in keeps the
   * existing estimate document contract unchanged; amounts still count. */
  respectLinePriceVisibility?: boolean;
  /** App-owned choices, placed once before the totals row (or at the end if
   * the design has no visible totals). Omitted for static previews/PDFs. */
  beforeTotals?: ReactNode;
  /**
   * The customer's timezone, used only for the "Times shown in …" note under the
   * estimate details (dates themselves render tz-agnostically). Omitted → no note.
   */
  timeZone?: string | null;
}

// ─── Content block renderers ────────────────────────────────────────────────

function LogoBlock({
  block,
  layout,
  company,
}: {
  block: EstimateDocContentBlock;
  layout: EstimateDocLayout;
  company: CompanyInfo | null;
}) {
  const co = brandedCompany(company, layout.branding);
  const logoUrl = layout.branding?.logoUrl ?? co?.logoUrl ?? null;
  const name = co?.name ?? 'Your Company';
  const tagline = co?.tagline || '';
  // Initials for the fallback mark: first letters of the first two words.
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('') || 'N';
  // The logo is a flex row, so alignment is a `justify-*`, not `text-align`.
  const logoJustify =
    block.align === 'center'
      ? 'justify-center'
      : block.align === 'right'
        ? 'justify-end'
        : 'justify-start';
  return (
    <BlockShell block={block}>
      <div className={`flex items-center gap-[9px] ${logoJustify}`}>
        {logoUrl ? (
          <img src={logoUrl} alt={name} className="h-[38px] w-auto max-w-[220px] object-contain" />
        ) : (
          <>
            <span
              className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[7px] text-white"
              style={{
                background: 'var(--doc-primary)',
                fontFamily: 'var(--doc-heading-font)',
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.02em',
              }}
            >
              {initials}
            </span>
            <span
              style={{
                fontSize: '12.5px',
                fontWeight: 600,
                lineHeight: 1.15,
                letterSpacing: '-0.015em',
                color: INK,
              }}
            >
              {name}
            </span>
          </>
        )}
      </div>
      {tagline ? (
        <div className="mt-[6px]" style={{ fontSize: '10px', color: MUTED }}>
          {tagline}
        </div>
      ) : null}
    </BlockShell>
  );
}

function CompanyInfoBlock({
  block,
  layout,
  company,
}: {
  block: EstimateDocContentBlock;
  layout: EstimateDocLayout;
  company: CompanyInfo | null;
}) {
  const co = brandedCompany(company, layout.branding);
  const addressLines = brandedAddressLines(company, layout.branding);
  const license = co?.contractorLicense || '';
  return (
    <BlockShell block={block}>
      {isFieldVisible(block, 'name') ? (
        <div
          style={{
            ...valueStyle(block),
            fontFamily: 'var(--doc-heading-font)',
            fontSize: '10.5px',
            fontWeight: 600,
            color: INK,
          }}
        >
          {co?.name ?? 'Your Company'}
        </div>
      ) : null}
      {isFieldVisible(block, 'address')
        ? addressLines.map((line, i) => (
            <div
              key={`${line}-${i}`}
              style={{ fontSize: '9.5px', lineHeight: 1.65, color: MUTED, ...valueStyle(block) }}
            >
              {line}
            </div>
          ))
        : null}
      {isFieldVisible(block, 'phone') && co?.phone ? (
        <div style={{ fontSize: '9.5px', lineHeight: 1.65, color: MUTED, ...valueStyle(block) }}>
          {formatPhoneDisplay(co.phone)}
        </div>
      ) : null}
      {isFieldVisible(block, 'email') && co?.email ? (
        <div style={{ fontSize: '9.5px', lineHeight: 1.65, color: MUTED, ...valueStyle(block) }}>
          {co.email}
        </div>
      ) : null}
      {isFieldVisible(block, 'website') && co?.website ? (
        <div style={{ fontSize: '9.5px', lineHeight: 1.65, color: MUTED, ...valueStyle(block) }}>
          {co.website}
        </div>
      ) : null}
      {co?.taxRegistrationNumber ? (
        <div style={{ fontSize: '9px', color: MUTED, ...valueStyle(block) }}>
          Tax #: {co.taxRegistrationNumber}
        </div>
      ) : null}
      {co?.regulatoryNotice ? (
        <div
          style={{
            fontSize: '10px',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            marginTop: 4,
          }}
        >
          {co.regulatoryNotice}
        </div>
      ) : null}
      {license ? (
        <div style={{ fontSize: '9px', color: MUTED, ...valueStyle(block) }}>License {license}</div>
      ) : null}
    </BlockShell>
  );
}

function BillToBlock({ block, estimate }: { block: EstimateDocContentBlock; estimate: Estimate }) {
  // Bill-to CONTACT wins over the job customer (they can differ — a distinct
  // billing party); only a customer billed to itself borrows its own details.
  const { name, email, phone, addressLines } = estimateBillToParty(estimate);
  const vCss = valueStyle(block);
  return (
    <BlockShell block={block}>
      <SectionLabel style={labelStyle(block)}>
        {fieldLabel(block, 'section', 'Bill To')}
      </SectionLabel>
      {isFieldVisible(block, 'name') ? (
        <div style={{ fontSize: '11.5px', fontWeight: 600, color: INK, ...vCss }}>{name}</div>
      ) : null}
      {isFieldVisible(block, 'email') && email ? (
        <div style={{ fontSize: '10px', lineHeight: 1.6, color: MUTED, ...vCss }}>{email}</div>
      ) : null}
      {isFieldVisible(block, 'phone') && phone ? (
        <div style={{ fontSize: '10px', lineHeight: 1.6, color: MUTED, ...vCss }}>
          {formatPhoneDisplay(phone)}
        </div>
      ) : null}
      {isFieldVisible(block, 'address') && addressLines.length > 0
        ? addressLines.map((line, i) => (
            <div
              key={`${line}-${i}`}
              style={{ fontSize: '10px', lineHeight: 1.6, color: MUTED, ...vCss }}
            >
              {line}
            </div>
          ))
        : null}
    </BlockShell>
  );
}

function JobAddressBlock({
  block,
  estimate,
}: {
  block: EstimateDocContentBlock;
  estimate: Estimate;
}) {
  const lines = estimateAddressLines(estimate.serviceAddress);
  return (
    <BlockShell block={block}>
      <SectionLabel style={labelStyle(block)}>
        {fieldLabel(block, 'section', 'Service Address')}
      </SectionLabel>
      {lines.length > 0 ? (
        lines.map((line, i) => (
          <div
            key={`${line}-${i}`}
            style={{ fontSize: '10px', lineHeight: 1.6, color: INK_SOFT, ...valueStyle(block) }}
          >
            {line}
          </div>
        ))
      ) : (
        <div style={{ fontSize: '10px', color: MUTED, ...valueStyle(block) }}>—</div>
      )}
    </BlockShell>
  );
}

function formatDocDate(value?: string | null): string {
  if (!value) return '—';
  // Business dates go through the sanctioned formatter. This document has no
  // per-entity timezone in scope yet (PR3a is pure/context-free), so it formats
  // tz-agnostically — exactly as EstimatePDF's `fmtDate` does when handed no
  // timeZone. A later PR that threads the entity tz through can pass it here.
  return (
    formatDateTimeInTimeZone(value, null, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }) || value
  );
}

function EstimateDetailsBlock({
  block,
  estimate,
  timeZone,
}: {
  block: EstimateDocContentBlock;
  estimate: Estimate;
  timeZone?: string | null;
}) {
  const tzLabel = timeZone ? formatTimeZoneLabel(timeZone) : '';
  const lCss = labelStyle(block);
  const vCss = valueStyle(block);
  const customerTitle = estimateCustomerVisibleTitle(estimate);
  const jobType = estimate.jobTypeName?.trim();
  const poNumber = estimate.poNumber?.trim();
  return (
    <BlockShell block={block}>
      <SectionLabel style={lCss}>{fieldLabel(block, 'section', 'Estimate')}</SectionLabel>
      {isFieldVisible(block, 'estimateNumber') && estimate.estimateNumber ? (
        <DetailRow
          label={fieldLabel(block, 'estimateNumber', 'Estimate #')}
          value={estimate.estimateNumber}
          labelCss={lCss}
          valueCss={vCss}
        />
      ) : null}
      {isFieldVisible(block, 'title') && customerTitle ? (
        <DetailRow
          label={fieldLabel(block, 'title', 'Title')}
          value={customerTitle}
          labelCss={lCss}
          valueCss={vCss}
        />
      ) : null}
      {isFieldVisible(block, 'jobType') && jobType ? (
        <DetailRow
          label={fieldLabel(block, 'jobType', 'Job Type')}
          value={jobType}
          labelCss={lCss}
          valueCss={vCss}
        />
      ) : null}
      {isFieldVisible(block, 'poNumber') && poNumber ? (
        <DetailRow
          label={fieldLabel(block, 'poNumber', 'PO #')}
          value={poNumber}
          labelCss={lCss}
          valueCss={vCss}
        />
      ) : null}
      {isFieldVisible(block, 'date') ? (
        <DetailRow
          label={fieldLabel(block, 'date', 'Date of Issue')}
          value={formatDocDate(estimate.createdAt)}
          labelCss={lCss}
          valueCss={vCss}
        />
      ) : null}
      {isFieldVisible(block, 'validUntil') && estimate.validUntil ? (
        <DetailRow
          label={fieldLabel(block, 'validUntil', 'Valid Until')}
          // `@db.Date` calendar day — never a zone conversion. See date-only.ts.
          value={formatDateOnly(estimate.validUntil)}
          labelCss={lCss}
          valueCss={vCss}
        />
      ) : null}
      {tzLabel ? (
        <div style={{ fontSize: '8.5px', color: MUTED, marginTop: '3px', ...lCss }}>
          Times shown in {tzLabel}
        </div>
      ) : null}
    </BlockShell>
  );
}

function ItemGroupsBlock({
  block,
  money,
  allLines,
  membershipOffer,
  respectLinePriceVisibility = false,
}: {
  block: EstimateDocContentBlock;
  money: EstimateDocMoney;
  /** Every priced line on the estimate — the base a member adjustment matches against. */
  allLines: Estimate['lineItems'];
  /** Present only when the customer has accepted the offer; drives the strikethrough. */
  membershipOffer: EstimateMembershipOffer | null;
  respectLinePriceVisibility?: boolean;
}) {
  const showDescription = isFieldVisible(block, 'description');
  const showImage = isFieldVisible(block, 'itemImage');
  const showQty = isFieldVisible(block, 'quantity');
  const showUnitPrice = isFieldVisible(block, 'unitPrice');
  const showAmount = isFieldVisible(block, 'lineAmount');
  const showComponents = isFieldVisible(block, 'components');
  // An optional add-on appears in the billed table ONLY once the customer
  // selects it (then it reads as an ordinary line, since it's now part of what
  // they're buying). An unselected optional lives solely in the "Optional
  // Services" selector below the document — never as a "not included" row here.
  const itemRows = money.rows.filter((r) => r.isSelected);
  const lCss = labelStyle(block);
  const vCss = valueStyle(block);

  const headCell: CSSProperties = {
    fontFamily: MONO,
    fontSize: '8px',
    fontWeight: 500,
    letterSpacing: '0.13em',
    textTransform: 'uppercase',
  };
  const numCell: CSSProperties = { fontFamily: MONO, fontSize: '10px', color: INK_SOFT };

  return (
    <BlockShell block={block}>
      {/* Only the description may break anywhere (a pasted URL or an unbroken
          word must not push the table wide). The number columns never wrap:
          with wrapping allowed everywhere, one long description made the
          browser starve QTY / UNIT PRICE / AMOUNT down to a character per
          line and stretch the dark header to match. */}
      <table className="w-full border-collapse">
        <thead>
          <tr style={{ background: 'var(--doc-primary)', color: '#ffffff' }} className="text-left">
            <th className="rounded-l-[5px] py-[6px] pl-3 pr-3" style={{ ...headCell, ...lCss }}>
              {fieldLabel(block, 'description', 'Description')}
            </th>
            {showQty ? (
              <th
                className="whitespace-nowrap px-3 py-[6px] text-right"
                style={{ ...headCell, ...lCss }}
              >
                {fieldLabel(block, 'quantity', 'Qty')}
              </th>
            ) : null}
            {showUnitPrice ? (
              <th
                className="whitespace-nowrap px-3 py-[6px] text-right"
                style={{ ...headCell, ...lCss }}
                data-col="unitPrice"
              >
                {fieldLabel(block, 'unitPrice', 'Unit Price')}
              </th>
            ) : null}
            {showAmount ? (
              <th
                className="whitespace-nowrap rounded-r-[5px] py-[6px] pl-3 pr-3 text-right"
                style={{ ...headCell, ...lCss }}
              >
                {fieldLabel(block, 'lineAmount', 'Amount')}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {itemRows.map(({ row }, i) => {
            const item = row.line;
            // The catalog item's own "what the customer sees on the line" choice
            // narrows what the block-level toggles allow (see lineDetailVisibility).
            const detail = lineDetailVisibility(item.displayConfig);
            const media = item as { imageUrl?: string | null; imagePreviewUrl?: string | null };
            const imgUrl = media.imagePreviewUrl || media.imageUrl || null;
            const amounts = resolveEstimateDocRowAmounts(row);
            const hidePrice = respectLinePriceVisibility && item.displayConfig?.hidePrice === true;
            // When the customer accepts the membership offer, an affected line
            // strikes its list price and prints the member price + savings —
            // the identical derivation the classic itemized document uses.
            const memberAdjustment = membershipOffer
              ? membershipOfferAdjustmentForLines(membershipOffer, row.lines, allLines)
              : null;
            const displayedUnitPrice =
              memberAdjustment && amounts.quantity > 0
                ? (memberAdjustment.memberTotal - memberAdjustment.memberTax) / amounts.quantity
                : amounts.effectiveUnitPrice;
            // Pre-tax, like the unit price beside it: tax is stated once, in the
            // totals block.
            const displayedTotal = memberAdjustment
              ? memberAdjustment.memberTotal - memberAdjustment.memberTax
              : amounts.printedAmount;
            const priceText = hidePrice
              ? '—'
              : amounts.showsAmount
                ? fmtMoney(displayedUnitPrice)
                : INCLUDED_PRICE_DISPLAY;
            const totalText = hidePrice
              ? '—'
              : amounts.showsAmount
                ? fmtMoney(displayedTotal)
                : INCLUDED_PRICE_DISPLAY;
            return (
              <tr
                key={`${item.lineItemId}-${i}`}
                className="align-top"
                style={{ borderBottom: `1px solid ${HAIRLINE}` }}
              >
                <td className="py-[9px] pl-3 pr-3 [overflow-wrap:anywhere]">
                  <div className="flex items-start gap-[9px]">
                    {showImage && imgUrl ? (
                      <img
                        src={imgUrl}
                        alt=""
                        className="h-[36px] w-[36px] shrink-0 rounded-[4px] object-cover"
                        style={{ border: `1px solid ${HAIRLINE}` }}
                      />
                    ) : null}
                    <div className="min-w-0">
                      <div style={{ fontSize: '10.5px', fontWeight: 500, color: INK, ...vCss }}>
                        {item.name}
                      </div>
                      {showDescription && detail.description && item.description ? (
                        <div style={{ fontSize: '9.5px', color: MUTED, marginTop: '1px', ...vCss }}>
                          <RichTextContent content={item.description} />
                        </div>
                      ) : null}
                      {showComponents && detail.included && row.includedNames.length > 0 ? (
                        <ul className="mt-1 space-y-0.5">
                          {row.includedNames.map((child, idx) => (
                            <li
                              key={`${child.name}-${idx}`}
                              className="flex items-baseline justify-between gap-2"
                              style={{ fontSize: '9.5px', color: MUTED }}
                            >
                              <span>
                                • {child.name}
                                {showQty && child.quantity > 1 ? ` × ${child.quantity}` : ''}
                              </span>
                              <span>{child.isOptional ? 'optional' : 'included'}</span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                  </div>
                </td>
                {showQty ? (
                  <td
                    className="whitespace-nowrap px-3 py-[9px] text-right"
                    style={{ ...numCell, ...vCss }}
                  >
                    {formatQuantityWithUnit(amounts.quantity, item.unit)}
                  </td>
                ) : null}
                {showUnitPrice ? (
                  <td
                    className="whitespace-nowrap px-3 py-[9px] text-right"
                    style={{ ...numCell, ...vCss }}
                    data-col="unitPrice"
                  >
                    {!hidePrice &&
                    amounts.showsAmount &&
                    memberAdjustment &&
                    memberAdjustment.savings > 0 ? (
                      <div className="flex flex-col items-end">
                        <span
                          style={{ color: STRIKE, textDecoration: 'line-through', fontSize: '9px' }}
                        >
                          {fmtMoney(amounts.effectiveUnitPrice)}
                        </span>
                        <span style={{ color: INK, fontWeight: 500 }}>{priceText}</span>
                      </div>
                    ) : (
                      priceText
                    )}
                  </td>
                ) : null}
                {showAmount ? (
                  <td
                    className="whitespace-nowrap py-[9px] pl-3 pr-3 text-right"
                    style={{ ...numCell, fontSize: '10.5px', fontWeight: 500, color: INK, ...vCss }}
                  >
                    {!hidePrice &&
                    amounts.showsAmount &&
                    memberAdjustment &&
                    memberAdjustment.savings > 0 ? (
                      <div className="flex flex-col items-end">
                        <span
                          style={{
                            color: STRIKE,
                            textDecoration: 'line-through',
                            fontSize: '9px',
                            fontWeight: 400,
                          }}
                        >
                          {fmtMoney(amounts.printedAmount)}
                        </span>
                        <span>{totalText}</span>
                        <span style={{ color: MEMBER_SAVE, fontSize: '8.5px', fontWeight: 500 }}>
                          Member price · save {fmtMoney(memberAdjustment.savings)}
                        </span>
                      </div>
                    ) : (
                      totalText
                    )}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </BlockShell>
  );
}

function TotalsBlock({
  block,
  money,
  estimate,
  override,
}: {
  block: EstimateDocContentBlock;
  money: EstimateDocMoney;
  estimate: Estimate;
  override?: EstimateDocTotalsOverride;
}) {
  const lCss = labelStyle(block);
  const vCss = valueStyle(block);
  const numStyle: CSSProperties = { fontFamily: MONO, fontSize: '10.5px', fontWeight: 500 };
  const collected = estimate.depositCollected ? estimate.depositCollectedSummary : null;
  // Prefer the customer page's authoritative totals; otherwise derive from money
  // (builder preview — no membership, no plan line).
  const t: EstimateDocTotalsOverride = override ?? {
    subtotal: money.subtotal - (money.membershipSetupFee ?? 0),
    membershipSetupFee: money.membershipSetupFee,
    membershipSavings: money.membershipDiscountTotal,
    membershipPlanPrice: 0,
    membershipPlanLabel: 'Membership',
    discountTotal: money.discountTotal,
    discountLabel: fieldLabel(block, 'discount', 'Discount'),
    taxBreakdown: money.taxBreakdown,
    taxTotal: money.taxTotal,
    taxableSubtotal: money.taxableSubtotal,
    grandTotal: money.grandTotal,
    depositAmount: money.depositAmount,
  };
  return (
    <BlockShell block={block}>
      <div className="ml-auto w-full max-w-[262px]">
        <DetailRow
          label={fieldLabel(block, 'subtotal', 'Subtotal')}
          value={fmtMoney(t.subtotal)}
          labelCss={{ fontSize: '10px', color: INK_SOFT, ...lCss }}
          valueCss={{ ...numStyle, color: INK, ...vCss }}
        />
        {t.membershipSavings > 0 ? (
          <DetailRow
            label={
              t.membershipPlanPrice > 0
                ? `${fieldLabel(block, 'membershipSavings', 'Membership savings')} (${t.membershipSavingsLabel ?? t.membershipPlanLabel})`
                : fieldLabel(block, 'membershipSavings', 'Membership savings')
            }
            value={`-${fmtMoney(t.membershipSavings)}`}
            labelCss={{ fontSize: '10px', color: '#2e4a2e', ...lCss }}
            valueCss={{ ...numStyle, color: '#2e4a2e', ...vCss }}
          />
        ) : null}
        {t.membershipPlanPrice > 0 ? (
          <DetailRow
            label={t.membershipPlanLabel}
            value={fmtMoney(t.membershipPlanPrice)}
            labelCss={{ fontSize: '10px', color: INK_SOFT, ...lCss }}
            valueCss={{ ...numStyle, color: INK, ...vCss }}
          />
        ) : null}
        {!!t.membershipSetupFee && (
          <DetailRow label="One-time setup fee" value={fmtMoney(t.membershipSetupFee)} />
        )}
        {t.discountTotal > 0 ? (
          <DetailRow
            label={t.discountLabel}
            value={`-${fmtMoney(t.discountTotal)}`}
            labelCss={{ fontSize: '10px', color: INK_SOFT, ...lCss }}
            valueCss={{ ...numStyle, color: INK, ...vCss }}
          />
        ) : null}
        {t.taxTotal > 0 && t.taxableSubtotal !== undefined ? (
          <DetailRow
            label="Taxable Subtotal"
            value={fmtMoney(t.taxableSubtotal)}
            labelCss={{ fontSize: '10px', color: INK_SOFT, ...lCss }}
            valueCss={{ ...numStyle, color: INK, ...vCss }}
          />
        ) : null}
        {t.taxBreakdown.length > 0 ? (
          t.taxBreakdown.map((tb, i) => (
            <div key={`${tb.primary}-${tb.secondary ?? ''}-${tb.rate}-${i}`}>
              <DetailRow
                label={taxBreakdownDisplayLabel(tb)}
                value={fmtMoney(tb.amount)}
                labelCss={{ fontSize: '10px', color: INK_SOFT, ...lCss }}
                valueCss={{ ...numStyle, color: INK, ...vCss }}
              />
              {tb.registrationNumber ? (
                <div style={{ fontSize: '8.5px', color: MUTED, ...lCss }}>
                  Reg #: {tb.registrationNumber}
                </div>
              ) : null}
            </div>
          ))
        ) : (
          <DetailRow
            label={fieldLabel(block, 'tax', 'Tax')}
            value={fmtMoney(t.taxTotal)}
            labelCss={{ fontSize: '10px', color: INK_SOFT, ...lCss }}
            valueCss={{ ...numStyle, color: INK, ...vCss }}
          />
        )}
        <div style={{ fontSize: '8.5px', color: MUTED, marginTop: '2px', ...lCss }}>
          Final taxes may be recalculated when invoiced.
        </div>
        <div
          className="mt-1 flex items-baseline justify-between gap-4 pt-2"
          style={{ borderTop: `1.5px solid ${INK}` }}
        >
          <span style={{ fontSize: '11px', fontWeight: 600, color: INK, ...lCss }}>
            {fieldLabel(block, 'total', 'Total')}
          </span>
          <span
            style={{
              fontFamily: MONO,
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '-0.02em',
              color: 'var(--doc-primary)',
              ...vCss,
            }}
          >
            {fmtMoney(t.grandTotal)}
          </span>
        </div>
        {t.depositAmount > 0 ? (
          <DetailRow
            label={fieldLabel(block, 'deposit', 'Deposit')}
            value={fmtMoney(t.depositAmount)}
            labelCss={{ fontSize: '10px', color: INK_SOFT, ...lCss }}
            valueCss={{ ...numStyle, color: INK, ...vCss }}
          />
        ) : null}
        {collected ? (
          <div className="mt-2 border-t pt-1" style={{ borderColor: LINE }}>
            <DetailRow
              label="Deposit collected"
              value={fmtMoney(collected.chargedAmount)}
              labelCss={{ fontSize: '10px', color: INK_SOFT, ...lCss }}
              valueCss={{ ...numStyle, color: INK, ...vCss }}
            />
            {(collected.discountAmount ?? 0) > 0 ? (
              <DetailRow
                label="ACH/check discount"
                value={fmtMoney(collected.discountAmount!)}
                labelCss={{ fontSize: '10px', color: INK_SOFT, ...lCss }}
                valueCss={{ ...numStyle, color: INK, ...vCss }}
              />
            ) : null}
            {collected.coveredAmount != null ? (
              <DetailRow
                label="Deposit covered"
                value={fmtMoney(collected.coveredAmount)}
                labelCss={{ fontSize: '10px', color: INK_SOFT, ...lCss }}
                valueCss={{ ...numStyle, color: INK, ...vCss }}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </BlockShell>
  );
}

// The deposit renders as a line in the totals block (like the classic document),
// so the standalone deposit block draws nothing — no forced full-width box.
function DepositBlock(_props: { block: EstimateDocContentBlock; money: EstimateDocMoney }) {
  return null;
}

function AttachmentsBlock({
  block,
  attachments,
}: {
  block: EstimateDocContentBlock;
  attachments: EstimateDocAttachment[];
}) {
  if (attachments.length === 0) return null;
  return (
    <BlockShell block={block}>
      {/* Bold heading (matches BlockShell's block heading used by notes/terms) so
          the bottom content sections read as one set. Attachments has no
          `block.heading`, so the section field supplies the title text. */}
      <h2
        className="mb-[9px]"
        style={{
          fontSize: '13px',
          fontWeight: 600,
          letterSpacing: '-0.015em',
          color: INK,
          fontFamily: 'var(--doc-heading-font)',
        }}
      >
        {fieldLabel(block, 'section', 'Attachments')}
      </h2>
      <div className="grid grid-cols-3 gap-[8px]">
        {attachments.map((a) => (
          <AttachmentTile key={a.id} attachment={a} />
        ))}
      </div>
    </BlockShell>
  );
}

/**
 * One attachment: the tile opens/downloads the file, and a preview that fails
 * to load (CORS, an unsupported format) degrades to a "tap to open" tile
 * instead of a broken image (NOT-243).
 */
function AttachmentTile({ attachment: a }: { attachment: EstimateDocAttachment }) {
  const [failed, setFailed] = useState(false);
  return (
    <a
      href={a.url}
      download={a.fileName ?? undefined}
      target="_blank"
      rel="noreferrer"
      className="block overflow-hidden rounded-[6px]"
      style={{ border: `1px solid ${LINE}` }}
    >
      {failed ? (
        <div
          className="flex h-[84px] w-full items-center justify-center px-2 text-center"
          style={{ fontSize: '8.5px', color: MUTED, background: '#fafaf8' }}
        >
          Couldn't preview — tap to open
        </div>
      ) : (
        <img
          src={a.url}
          alt={a.fileName ?? ''}
          className="block h-[84px] w-full object-cover"
          onError={() => setFailed(true)}
        />
      )}
      {a.fileName ? (
        <div className="truncate px-[6px] py-[3px]" style={{ fontSize: '8.5px', color: MUTED }}>
          {a.fileName}
        </div>
      ) : null}
    </a>
  );
}

function ScopeOfWorkBlock({
  block,
  estimate,
}: {
  block: EstimateDocContentBlock;
  estimate: Estimate;
}) {
  const scope = estimate.scopeOfWork;
  if (!hasReadableContent(scope)) return null;
  const html = scope ?? '';
  return (
    <BlockShell block={block}>
      <h2
        className="mb-[9px]"
        style={{
          fontSize: '13px',
          fontWeight: 600,
          letterSpacing: '-0.015em',
          color: INK,
          fontFamily: 'var(--doc-heading-font)',
        }}
      >
        {fieldLabel(block, 'section', 'Scope of Work')}
      </h2>
      {isHtmlContent(html) ? (
        <div
          className={`max-w-none ${RICH_TEXT_CLASS}`}
          style={{ fontSize: '10.5px', lineHeight: 1.65, color: INK_SOFT, ...valueStyle(block) }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
        />
      ) : (
        <p
          className="whitespace-pre-wrap"
          style={{ fontSize: '10.5px', lineHeight: 1.65, color: INK_SOFT, ...valueStyle(block) }}
        >
          {html}
        </p>
      )}
    </BlockShell>
  );
}

function NotesBlock({ block, estimate }: { block: EstimateDocContentBlock; estimate: Estimate }) {
  const notes = getEstimateCustomerNotes(estimate);
  if (!hasReadableContent(notes)) return null;
  // One title: the author-editable heading, which BlockShell renders when set.
  // Older templates stored their title in `fields.section.label` (no heading) —
  // fall back to it here so those headings are never lost.
  return (
    <BlockShell block={block}>
      {!block.heading?.trim() ? (
        <BlockSectionHeading>{fieldLabel(block, 'section', 'Message')}</BlockSectionHeading>
      ) : null}
      {isHtmlContent(notes) ? (
        <div
          className={`max-w-none ${RICH_TEXT_CLASS}`}
          style={{ fontSize: '10.5px', lineHeight: 1.65, color: INK_SOFT, ...valueStyle(block) }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notes) }}
        />
      ) : (
        <p
          className="whitespace-pre-wrap"
          style={{ fontSize: '10.5px', lineHeight: 1.65, color: INK_SOFT, ...valueStyle(block) }}
        >
          {notes}
        </p>
      )}
    </BlockShell>
  );
}

function TermsBlock({ block, estimate }: { block: EstimateDocContentBlock; estimate: Estimate }) {
  const terms = resolveCustomerTerms(estimate.termsAndConditions);
  if (!terms.hasTerms) return null;
  // One title: BlockShell renders `block.heading` when set; older templates
  // stored their title in `fields.section.label` (no heading) — fall back to it.
  return (
    <BlockShell block={block}>
      {!block.heading?.trim() ? (
        <BlockSectionHeading>
          {fieldLabel(block, 'section', 'Terms & Conditions')}
        </BlockSectionHeading>
      ) : null}
      <CustomerTermsBody terms={terms} className="text-[8.5px] leading-[1.6] text-[#6b6b70]" />
    </BlockShell>
  );
}

function AuthoredProseBlock({ block }: { block: EstimateDocContentBlock }) {
  return (
    <BlockShell block={block}>
      <DocRichText
        content={block.content}
        style={{ fontSize: '10.5px', lineHeight: 1.65, color: INK_SOFT, ...valueStyle(block) }}
      />
    </BlockShell>
  );
}

// ─── Interaction slots ──────────────────────────────────────────────────────
// A slot marks WHERE a customer action sits in the document. The app owns the
// behaviour; here we render a realistic, non-interactive preview so the author
// sees exactly what the customer will do at that spot.

const SLOT_CAPTION: Record<EstimateDocSlotBlock['type'], string> = {
  approveDecline: 'Customer action · approve or decline',
  payDeposit: 'Customer action · pay the deposit',
  signature: 'Customer action · signature',
  goodBetterBest: 'Customer action · choose an option',
  payInvoice: 'Customer action · pay the invoice',
};

const docBtnPrimary: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 15px',
  borderRadius: '6px',
  background: 'var(--doc-primary)',
  color: '#ffffff',
  fontSize: '10.5px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};
const docBtnGhost: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 15px',
  borderRadius: '6px',
  background: '#ffffff',
  border: `1px solid ${LINE}`,
  color: INK_SOFT,
  fontSize: '10.5px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
};

function SlotBody({
  type,
  money,
}: {
  type: EstimateDocSlotBlock['type'];
  money: EstimateDocMoney;
}) {
  if (type === 'approveDecline') {
    return (
      <div
        className="flex items-center gap-[10px] rounded-[8px] px-4 py-[13px]"
        style={{ background: '#f5f4ef' }}
      >
        <div className="min-w-0">
          <div style={{ fontSize: '11px', fontWeight: 600, color: INK }}>Ready to move ahead?</div>
          <div style={{ fontSize: '9.5px', color: MUTED, marginTop: '2px' }}>
            Approving reserves the work. Nothing is charged until the deposit is paid.
          </div>
        </div>
        <span className="flex-1" />
        <span style={docBtnGhost}>Decline</span>
        <span style={docBtnPrimary}>Approve estimate</span>
      </div>
    );
  }
  if (type === 'payDeposit') {
    const amt = money.depositAmount > 0 ? ` ${fmtMoney(money.depositAmount)}` : '';
    return (
      <div
        className="flex items-center gap-[10px] rounded-[8px] px-4 py-[13px]"
        style={{ background: '#f5f4ef' }}
      >
        <div style={{ fontSize: '10px', color: MUTED }}>Secure the schedule with a deposit.</div>
        <span className="flex-1" />
        <span style={docBtnPrimary}>Pay deposit{amt}</span>
      </div>
    );
  }
  if (type === 'payInvoice') {
    // Builder preview is sample-only; mirror the balanceSummary sample balance.
    return (
      <div
        className="flex items-center gap-[10px] rounded-[8px] px-4 py-[13px]"
        style={{ background: '#f5f4ef' }}
      >
        <div style={{ fontSize: '10px', color: MUTED }}>Pay the balance online.</div>
        <span className="flex-1" />
        <span style={docBtnPrimary}>Pay invoice $260.00</span>
      </div>
    );
  }
  if (type === 'signature') {
    return (
      <div className="flex items-end gap-[18px]">
        <div className="flex-1">
          <div style={{ height: '30px', borderBottom: `1px solid ${INK}` }} />
          <div className="mt-[4px]" style={docLabelStyle}>
            Signature
          </div>
        </div>
        <div className="w-[130px]">
          <div style={{ height: '30px', borderBottom: `1px solid ${INK}` }} />
          <div className="mt-[4px]" style={docLabelStyle}>
            Date
          </div>
        </div>
      </div>
    );
  }
  // goodBetterBest — proposal-only; a light marker for where the tiers render.
  return (
    <div className="grid grid-cols-3 gap-[8px]">
      {['Good', 'Better', 'Best'].map((t) => (
        <div
          key={t}
          className="rounded-[6px] px-2 py-3 text-center"
          style={{ border: `1px dashed ${LINE}`, fontSize: '9.5px', color: MUTED }}
        >
          {t}
        </div>
      ))}
    </div>
  );
}

function SlotPlaceholder({
  block,
  money,
}: {
  block: EstimateDocSlotBlock;
  money: EstimateDocMoney;
}) {
  return (
    <BlockShell block={block}>
      <div data-slot-type={block.type}>
        <div className="mb-[6px]" style={docLabelStyle}>
          {block.heading ?? SLOT_CAPTION[block.type]}
        </div>
        <SlotBody type={block.type} money={money} />
      </div>
    </BlockShell>
  );
}

// ─── Block dispatcher ───────────────────────────────────────────────────────

// ── INVOICE-only preview blocks ─────────────────────────────────────────────
// Sample content so the builder shows a realistic invoice while the author
// customises layout/labels/styling. The sent invoice fills real figures.
function InvoiceDetailsPreviewBlock({ block }: { block: EstimateDocContentBlock }) {
  const lCss = labelStyle(block);
  const vCss = valueStyle(block);
  const rows: Array<[string, string, string]> = [
    ['invoiceNumber', fieldLabel(block, 'invoiceNumber', 'Invoice #'), 'INV-000123'],
    ['status', fieldLabel(block, 'status', 'Status'), 'Open'],
    ['date', fieldLabel(block, 'date', 'Date'), 'Aug 12, 2026'],
    ['dueDate', fieldLabel(block, 'dueDate', 'Due Date'), 'Aug 26, 2026'],
    ['jobType', fieldLabel(block, 'jobType', 'Job Type'), 'HVAC Maintenance'],
    ['jobNumber', fieldLabel(block, 'jobNumber', 'Job #'), 'JOB-000123'],
    ['serviceDate', fieldLabel(block, 'serviceDate', 'Service Date'), 'Aug 10, 2026'],
  ];
  return (
    <BlockShell block={block}>
      {rows
        .filter(([key]) => isFieldVisible(block, key))
        .map(([key, label, value]) => (
          <DetailRow key={key} label={label} value={value} labelCss={lCss} valueCss={vCss} />
        ))}
    </BlockShell>
  );
}

function BalanceSummaryPreviewBlock({ block }: { block: EstimateDocContentBlock }) {
  return (
    <BlockShell block={block}>
      <div style={{ marginLeft: 'auto', width: '55%', minWidth: 180 }}>
        <DetailRow label="Amount Paid" value="-$120.00" />
        <DetailRow label="Credits Applied" value="-$0.00" />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 4,
            paddingTop: 4,
            borderTop: `1px solid ${LINE}`,
            fontWeight: 600,
            color: '#b42318',
          }}
        >
          <span style={{ fontSize: '10px' }}>Balance Due</span>
          <span style={{ fontSize: '10px' }}>$260.00</span>
        </div>
      </div>
    </BlockShell>
  );
}

function PaymentHistoryPreviewBlock({ block }: { block: EstimateDocContentBlock }) {
  return (
    <BlockShell block={block}>
      <SectionLabel>{block.heading || 'Payment History'}</SectionLabel>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '9.5px',
          color: MUTED,
          lineHeight: 1.7,
        }}
      >
        <span>Aug 15, 2026 · Card</span>
        <span>$120.00</span>
      </div>
    </BlockShell>
  );
}

function RemitToPreviewBlock({
  block,
  layout,
  company,
}: {
  block: EstimateDocContentBlock;
  layout: EstimateDocLayout;
  company: CompanyInfo | null;
}) {
  const co = brandedCompany(company, layout.branding);
  const lines = brandedAddressLines(company, layout.branding);
  return (
    <BlockShell block={block}>
      <SectionLabel>{block.heading || 'Remit To'}</SectionLabel>
      <div style={{ fontSize: '10px', fontWeight: 600, color: INK }}>
        {co?.name ?? 'Your Company'}
      </div>
      {lines.map((line, i) => (
        <div key={`${line}-${i}`} style={{ fontSize: '9.5px', lineHeight: 1.65, color: MUTED }}>
          {line}
        </div>
      ))}
    </BlockShell>
  );
}

function AdditionalDetailsPreviewBlock({ block }: { block: EstimateDocContentBlock }) {
  // Sample "Additional Details" — the customer-visible custom-field values the
  // sent invoice fills in (label: value rows).
  const rows = [
    ['PO number', '4021-A'],
    ['Warranty', '10-year parts, 2-year labor'],
    ['Equipment serial', 'NH-18SEER-4471'],
  ];
  return (
    <BlockShell block={block}>
      <SectionLabel>{block.heading || 'Additional Details'}</SectionLabel>
      {rows.map(([label, value]) => (
        <div key={label} style={{ display: 'flex', gap: '8px', marginTop: '3px' }}>
          <span style={{ width: '120px', fontSize: '9.5px', fontWeight: 600, color: INK }}>
            {label}:
          </span>
          <span style={{ flex: 1, fontSize: '9.5px', color: MUTED }}>{value}</span>
        </div>
      ))}
    </BlockShell>
  );
}

function renderContentBlock(
  block: EstimateDocContentBlock,
  props: EstimateDocumentRendererProps,
  money: EstimateDocMoney,
  totalsMoney: EstimateDocMoney,
): ReactNode {
  switch (block.type) {
    case 'logo':
      return <LogoBlock block={block} layout={props.layout} company={props.company} />;
    case 'companyInfo':
      return <CompanyInfoBlock block={block} layout={props.layout} company={props.company} />;
    case 'billTo':
      return <BillToBlock block={block} estimate={props.estimate} />;
    case 'jobAddress':
      return <JobAddressBlock block={block} estimate={props.estimate} />;
    case 'estimateDetails':
      return (
        <EstimateDetailsBlock block={block} estimate={props.estimate} timeZone={props.timeZone} />
      );
    case 'scopeOfWork':
      return <ScopeOfWorkBlock block={block} estimate={props.estimate} />;
    // INVOICE-only blocks. The builder preview runs on a sample ESTIMATE, so
    // these render realistic SAMPLE content (an author is customising layout /
    // labels / styling, not real invoice figures — the sent invoice fills them).
    case 'invoiceDetails':
      return <InvoiceDetailsPreviewBlock block={block} />;
    case 'balanceSummary':
      return <BalanceSummaryPreviewBlock block={block} />;
    case 'paymentHistory':
      return <PaymentHistoryPreviewBlock block={block} />;
    case 'remitTo':
      return <RemitToPreviewBlock block={block} layout={props.layout} company={props.company} />;
    case 'additionalDetails':
      return <AdditionalDetailsPreviewBlock block={block} />;
    case 'itemGroups':
      return (
        <ItemGroupsBlock
          block={block}
          money={money}
          allLines={props.estimate.lineItems ?? []}
          membershipOffer={props.showMemberPrice ? (props.membershipOffer ?? null) : null}
          respectLinePriceVisibility={props.respectLinePriceVisibility}
        />
      );
    case 'totals':
      return (
        <TotalsBlock
          block={block}
          money={totalsMoney}
          estimate={props.estimate}
          override={props.totalsOverride}
        />
      );
    case 'deposit':
      return <DepositBlock block={block} money={totalsMoney} />;
    case 'membershipOffer':
      // App-owned (accept toggle + billing selector) and re-prices the document
      // live on accept; rendered by the customer page, never as a design block.
      return null;
    case 'attachments':
      return <AttachmentsBlock block={block} attachments={props.attachmentImages ?? []} />;
    case 'notes':
      return <NotesBlock block={block} estimate={props.estimate} />;
    case 'terms':
      return <TermsBlock block={block} estimate={props.estimate} />;
    case 'authText':
    case 'ackText':
    case 'richText':
      return <AuthoredProseBlock block={block} />;
    default:
      return null;
  }
}

function renderBlock(
  block: EstimateDocBlock,
  props: EstimateDocumentRendererProps,
  money: EstimateDocMoney,
  totalsMoney: EstimateDocMoney,
): ReactNode {
  if (block.kind === 'slot') {
    // The page renders the real interactive slots from its own action rail and
    // passes renderSlots={false}, so the document column shows nothing here.
    return props.renderSlots === false ? null : <SlotPlaceholder block={block} money={money} />;
  }
  return renderContentBlock(block, props, money, totalsMoney);
}

/**
 * Render one block's node in isolation, using the exact same block renderers as
 * the document. Exported so the builder's editable canvas overlays drag/select
 * affordances on real document output instead of re-implementing it.
 */
// eslint-disable-next-line react-refresh/only-export-components -- a render helper (not a component) the builder's editable canvas reuses; HMR impact is nil.
export function renderEstimateDocBlock(
  block: EstimateDocBlock,
  props: EstimateDocumentRendererProps,
  money: EstimateDocMoney,
): ReactNode {
  // The builder canvas renders a single block against the sample estimate with
  // no accepted membership, so the totals money is the same as the row money.
  return renderBlock(block, props, money, money);
}

/**
 * Renders an estimate as a FLAT vertical document — the blocks of `layout` in
 * `order`, hidden blocks dropped — so reordering the layout reorders the output
 * and hiding a block removes it. This is the whole point of the block model:
 * the document honours the template, it is not a fixed set of anchors.
 *
 * Pure and self-contained: it takes the layout, the estimate and the company as
 * props and consumes no page context. Money is computed with the shared estimate
 * helpers (see `estimateDocMoney`), never re-derived here.
 *
 * PR3a: content blocks render live estimate material; the four interaction slots
 * render labelled visual placeholders. Their real behaviour is wired to the
 * page's QuoteActionContext in a later PR.
 */
export function EstimateDocumentRenderer(props: EstimateDocumentRendererProps) {
  const { layout, estimate, company } = props;
  // The line-item table renders the ORIGINAL estimate (so an accepted-membership
  // line can strike through its list price beside the member price). The totals
  // and deposit, however, must show the member-adjusted figures — computed from
  // the same `estimateWithAcceptedMembershipOffer` the classic document uses, so
  // both surfaces report identical numbers.
  const money = computeEstimateDocMoney(estimate);
  const acceptedOffer =
    props.showMemberPrice && props.membershipOffer?.planPrice != null
      ? props.membershipOffer
      : null;
  const totalsMoney = acceptedOffer
    ? computeEstimateDocMoney(estimateWithAcceptedMembershipOffer(estimate, acceptedOffer))
    : money;

  // Group the ordered blocks into horizontal rows: a block with `inline` set
  // shares the row of the block before it, so authors can compose side-by-side
  // columns (letterhead, a bill-to/service/details band, deposit + totals).
  // Grouping happens before dropping empty nodes so an `inline` block never
  // silently attaches to an unrelated block when its row-starter renders null.
  const rows: { block: EstimateDocBlock; node: ReactNode }[][] = [];
  [...layout.blocks]
    .filter((block) => !block.hidden)
    .sort((a, b) => a.order - b.order)
    .forEach((block) => {
      const entry = { block, node: renderBlock(block, props, money, totalsMoney) };
      if (!block.inline || rows.length === 0) rows.push([entry]);
      else rows[rows.length - 1].push(entry);
    });
  const renderRows = rows
    .map((row) => row.filter((entry) => entry.node !== null))
    .filter((row) => row.length > 0);
  const totalsRowIndex = renderRows.findIndex((row) =>
    row.some(({ block }) => block.kind === 'content' && block.type === 'totals'),
  );

  const co = brandedCompany(company, layout.branding);
  const license = co?.contractorLicense || '';
  const footerContact = [co?.name, license ? `License ${license}` : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <article
      data-estimate-document={estimate.id}
      className="mx-auto w-full max-w-[700px] rounded-[3px] bg-white px-5 pb-7 pt-7 [overflow-wrap:anywhere] sm:px-11 sm:pb-10 sm:pt-11"
      style={{
        ...brandingToCssVars(layout.branding),
        boxShadow: '0 1px 2px rgba(10,11,13,.12), 0 10px 28px rgba(10,11,13,.10)',
      }}
    >
      {!layout.blocks.some((block) => block.type === 'companyInfo' && !block.hidden) &&
      (layout.branding?.regulatoryNotice ?? company?.regulatoryNotice) ? (
        <div className="mb-4 whitespace-pre-wrap break-words text-xs leading-relaxed">
          {layout.branding?.licenseNumber || company?.contractorLicense ? (
            <p>License {layout.branding?.licenseNumber || company?.contractorLicense}</p>
          ) : null}
          <p>{layout.branding?.regulatoryNotice ?? company?.regulatoryNotice}</p>
        </div>
      ) : null}
      <div className="flex flex-col gap-[18px]">
        {renderRows.map((row, index) => (
          <Fragment key={row[0].block.id}>
            {index === totalsRowIndex ? props.beforeTotals : null}
            {row.length === 1 ? (
              <div key={row[0].block.id}>{row[0].node}</div>
            ) : (
              <div
                key={row[0].block.id}
                // Side-by-side on a sheet, stacked on a phone (a 3-up letterhead
                // row is unreadable at 375px).
                className="grid grid-cols-1 items-start gap-[20px] sm:[grid-template-columns:var(--doc-row-cols)]"
                style={
                  {
                    '--doc-row-cols': `repeat(${row.length}, minmax(0, 1fr))`,
                  } as CSSProperties
                }
              >
                {row.map((entry) => (
                  <div key={entry.block.id} className="min-w-0">
                    {entry.node}
                  </div>
                ))}
              </div>
            )}
          </Fragment>
        ))}
        {totalsRowIndex === -1 ? props.beforeTotals : null}
      </div>

      {props.documentTrailing ? <div className="mt-[16px]">{props.documentTrailing}</div> : null}

      {footerContact ? (
        <div
          className="mt-[16px] flex items-center gap-3 pt-[10px]"
          style={{ borderTop: `1px solid ${LINE}` }}
        >
          <span style={{ fontSize: '8.5px', color: MUTED }}>{footerContact}</span>
        </div>
      ) : null}
    </article>
  );
}
